import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { DialSessionsTable, FullScreenLoader } from '@Components/index';
import CommonConstants from '@Constants/common.constants';
import AdminCallLog from '@Containers/Report/AdminCallLog';
import ReportFiltersModal from '@Containers/Report/ReportFiltersModal';
import {
  PrintAndExportCallLogPayload,
  SessionsSummaryQuery,
} from '@Models/report.model';
import printReportsService from '@Services/printReports.service';
import { useGetSessionSummaryQuery } from '@Services/report.service';
import { BaseAlertProps, ReportTypes } from '@Typings/global.types';
import {
  downloadCsv,
  downloadPDF,
  getModalBtnComponent,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import useRoute from '@Utils/hooks/useRoute.hook';
import { getSessionSummaryRTKQuery } from '@Utils/report/report.utils';

import AdminDialSessionsProps from './AdminDialSessions.types';
import { GetRows, getListOfSessionIds } from './AdminDialSessions.utils';

const AdminDialSessions: FunctionComponent<AdminDialSessionsProps> = (
  props: AdminDialSessionsProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();
  const { triggerAlert } = useAlert();

  /** PROPS */
  const { newReportBtnProps } = props;

  /** STATES */
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [tableCheckboxChecked, setTableCheckboxChecked] = useState<
    readonly string[]
  >([]);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [sessionName, setSessionName] = useState<string | null>(
    query.get(CommonConstants.QUERY_PARAMS.REPORT.SESSION_NAME) as string
  );
  const [showLoader, setShowLoader] = useState(false);

  /** SYNCS THE SESSION NAME STATE ACCORDING TO QUERY PARAM */
  useEffect(() => {
    const sessionNameParam = query.get(
      CommonConstants.QUERY_PARAMS.REPORT.SESSION_NAME
    ) as string;
    setSessionName(sessionNameParam);
  }, [location.search]);

  /** QUERIES */
  const sessionSummaryQuery: SessionsSummaryQuery = useMemo(
    () => getSessionSummaryRTKQuery(location.search, true),
    [location.search]
  );

  /** RTK QUERY HOOK */
  const { data, isLoading, isFetching, error } = useGetSessionSummaryQuery(
    sessionSummaryQuery,
    {
      skip:
        !!sessionName &&
        !query.get(CommonConstants.QUERY_PARAMS.REPORT.REPORT_TYPE),
    }
  );

  /** LOADING STATE */
  const loading = isLoading || isFetching || showLoader;

  /**
   * Creates a payload for Print/Export service
   * @param ids sessionIds
   * @param printAll Whether or not it is a global action and not a bulk action
   * @returns payload for print/export
   */
  const createPayloadForPrintAndExport = (
    ids: string[] = [],
    printAll = false
  ) => {
    const exportPayload: PrintAndExportCallLogPayload = {
      ...sessionSummaryQuery,
    };
    if (ids && ids.length && !printAll) {
      exportPayload.sessionId = [...ids];
    }

    return exportPayload;
  };

  /**
   * Exports sessions summary as a csv file
   */
  const exportDialSessionsHandler = (ids: string[] = [], exportAll = false) => {
    setShowLoader(true);

    printReportsService
      .exportSessionSummaryReport(
        createPayloadForPrintAndExport(ids, exportAll)
      )
      .then((response) => {
        if (response.status === 200 && response.data) {
          downloadCsv(response.data, t('reports.report_filename'));
          setTableCheckboxChecked([]);
        }
      })
      .catch(() => {
        triggerAlert('GENERIC_ERROR_MESSAGE', setAlertState, 'error');
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  /**
   * Exports sessions summary as a pdf file
   */
  const printSessionSummaryHandler = (
    ids: string[] = [],
    bulkAction = false
  ) => {
    setShowLoader(true);

    printReportsService
      .printSessionSummary(createPayloadForPrintAndExport(ids, bulkAction))
      .then((response) => {
        if (response.status === 200) {
          if (response.data) {
            downloadPDF(response.data, t('reports.report_filename'));
            setTableCheckboxChecked([]);
          }
        }
      })
      .catch(() => {
        triggerAlert('GENERIC_ERROR_MESSAGE', setAlertState, 'error');
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  /** Table Modal Button Component Function
   * Returns the Modal Button component which opens modal when clicked.
   */
  const modalBtnComponent = (id: string, name: string): JSX.Element => {
    const onClick = () => {
      const params = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]: '',
        [CommonConstants.QUERY_PARAMS.REPORT.SESSION_ID]: id,
        [CommonConstants.QUERY_PARAMS.REPORT.SESSION_NAME]: name,
      });

      changeRoute(params);
      setSessionName(name);
    };

    return getModalBtnComponent({
      id,
      name,
      onClick,
    });
  };

  /** ROWS AND TOTAL_ROWS_COUNT */
  const { rows, groupedMap } = GetRows(data?.table, modalBtnComponent);
  const totalRowsCount = data?.table[0]?.fullCount
    ? parseInt(data?.table[0].fullCount, 10)
    : CommonConstants.INITIAL_ROWS_PER_PAGE;

  /** Show error for get query */
  useEffect(() => {
    if (error) {
      triggerAlert(error, setAlertState);
    }
  }, [error, triggerAlert]);

  /** OPEN FILTERS MODAL */
  const openFilters = () => {
    setModalOpen(true);
  };

  /** BACK BUTTON CLICK HANDLER */
  const backBtnOnClick = () => {
    setSessionName('');
    const params = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
        CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_DATE,
      [CommonConstants.QUERY_PARAMS.REPORT.SESSION_ID]: '',
      [CommonConstants.QUERY_PARAMS.REPORT.SESSION_NAME]: '',
    });

    changeRoute(params);
  };

  return isLoading ? (
    <FullScreenLoader />
  ) : (
    <>
      {!sessionName ? (
        <>
          <ReportFiltersModal
            isAdmin
            initialReportType={ReportTypes.Sessions}
            open={modalOpen}
            setOpen={setModalOpen}
          />
          <DialSessionsTable
            alertState={alertState}
            exportAsPdfBtnProps={{
              onClick: () =>
                printSessionSummaryHandler(
                  getListOfSessionIds(tableCheckboxChecked, groupedMap)
                ),
              disabled: loading,
            }}
            exportAsCsvBtnProps={{
              onClick: () =>
                exportDialSessionsHandler(
                  getListOfSessionIds(tableCheckboxChecked, groupedMap)
                ),
              disabled: loading,
            }}
            exportAllBtnProps={{
              disabled: loading,
            }}
            exportAllAsCsvOnClick={() => exportDialSessionsHandler([], true)}
            exportAllAsPdfOnClick={() => printSessionSummaryHandler([], true)}
            showFiltersBtnProps={{
              onClick: openFilters,
            }}
            adminTableProps={{
              rows,
              checked: tableCheckboxChecked,
              setChecked: setTableCheckboxChecked,
              paginationProps: {
                count: totalRowsCount,
              },
              loading: isLoading || isFetching,
            }}
            {...props}
          />
        </>
      ) : (
        <AdminCallLog
          heading={`${t('reports.Call Log Report for')} ${sessionName}`}
          newReportBtnProps={newReportBtnProps}
          backBtnProps={{
            text: t('common.messages.Back'),
            onClick: backBtnOnClick,
          }}
        />
      )}
    </>
  );
};

export default AdminDialSessions;
